/**
 * siteLayerEditor — pure helpers for manual overlay layer CRUD on
 * `site.siteLayers`. KMZ-imported layers use the same SiteLayer shape;
 * manual layers are tagged `source: 'manual'`.
 */
import { LAYER_COLORS, AURORA_OVERLAY_PALETTE } from '../config/siteLayerColors';
import { dedupSiteLayers } from './siteLayers/dedupSiteLayers';
import { computeLayerContentHash } from '../services/kmzParser';
import type { SiteLayer, SiteLayerCategory } from '../types/site';

export type LayerGeometryKind = 'polygon' | 'line' | 'point';

/**
 * Preset swatches the "Edit Map Layers" colour picker offers. These are the
 * AURORA overlay palette (see {@link AURORA_OVERLAY_PALETTE}) plus two neutrals
 * (white, navy) for high-contrast outlines — so the presets a user picks from
 * match the same Aurora hues the overlays DEFAULT to. Picking a swatch writes
 * the chosen hex to `layer.color`, which then WINS over the category default
 * (the override tier in `colorForCategory`) — the Aurora palette only changes
 * what the default is, never what a user has explicitly chosen.
 *
 * Order mirrors the Aurora overlay legend: teal (accent), cyan, violet, amber,
 * rose, green, orange, slate — then the two neutrals.
 */
export const LAYER_COLOR_PRESETS: readonly string[] = [
  AURORA_OVERLAY_PALETTE.teal,   // #2dd4bd — Aurora accent
  AURORA_OVERLAY_PALETTE.cyan,   // #22d3ee
  AURORA_OVERLAY_PALETTE.violet, // #a78bfa
  AURORA_OVERLAY_PALETTE.amber,  // #fbbf24
  AURORA_OVERLAY_PALETTE.rose,   // #fb7185
  AURORA_OVERLAY_PALETTE.green,  // #34d399
  AURORA_OVERLAY_PALETTE.orange, // #fb923c
  AURORA_OVERLAY_PALETTE.slate,  // #94a3b8
  '#ffffff',                     // white  — high-contrast outline
  '#1e293b',                     // navy   — dark outline on light tiles
];

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

export function newManualLayerId(): string {
  return `layer-manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function normalizeLayerColor(
  color: string | undefined,
  category: SiteLayerCategory,
): string {
  if (color && HEX_COLOR.test(color.trim())) {
    return color.trim().toLowerCase();
  }
  return LAYER_COLORS[category];
}

export function minVerticesForGeometry(type: LayerGeometryKind): number {
  if (type === 'point') return 1;
  if (type === 'line') return 2;
  return 3;
}

export function latLngFromGooglePath(
  path: google.maps.MVCArray<google.maps.LatLng>,
): { lat: number; lng: number }[] {
  const out: { lat: number; lng: number }[] = [];
  for (let i = 0; i < path.getLength(); i++) {
    const p = path.getAt(i);
    out.push({ lat: p.lat(), lng: p.lng() });
  }
  return out;
}

export function createManualLayer(params: {
  name: string;
  category: SiteLayerCategory;
  geometryType: 'polygon' | 'line';
  coordinates: { lat: number; lng: number }[];
  color?: string;
  description?: string;
  id?: string;
}): SiteLayer {
  const category = params.category;
  const importedAt = new Date().toISOString();
  const layer: SiteLayer = {
    id: params.id ?? newManualLayerId(),
    name: params.name.trim() || 'Untitled layer',
    category,
    geometry: {
      type: params.geometryType,
      coordinates: params.coordinates,
    },
    description: params.description,
    source: 'manual',
    importedAt,
    visible: true,
    color: normalizeLayerColor(params.color, category),
    evidence: {
      use: 'site-truth',
      source: { providerId: 'manual', retrievedAt: importedAt },
    },
  };
  return stampLayerContentHash(layer);
}

export function stampLayerContentHash(layer: SiteLayer): SiteLayer {
  return {
    ...layer,
    contentHash: computeLayerContentHash(layer),
  };
}

export function upsertSiteLayer(layers: SiteLayer[], layer: SiteLayer): SiteLayer[] {
  const stamped = stampLayerContentHash(layer);
  const idx = layers.findIndex(l => l.id === stamped.id);
  if (idx < 0) return [...layers, stamped];
  const next = [...layers];
  next[idx] = stamped;
  return next;
}

export function removeSiteLayer(layers: SiteLayer[], id: string): SiteLayer[] {
  return layers.filter(l => l.id !== id);
}

/** Remove several layers at once (bulk delete). */
export function removeSiteLayers(layers: SiteLayer[], ids: readonly string[]): SiteLayer[] {
  const drop = new Set(ids);
  return layers.filter(l => !drop.has(l.id));
}

/**
 * Duplicate the given layers as new manual copies (bulk copy). Each copy gets a
 * fresh id, a "(copy)" name, and drops the canvas link (transaction-owned).
 */
export function duplicateSiteLayers(layers: SiteLayer[], ids: readonly string[]): SiteLayer[] {
  const pick = new Set(ids);
  const now = new Date().toISOString();
  const copies = layers
    .filter(l => pick.has(l.id))
    .map((l) => {
      const { linkedCanvasNodeId: _drop, ...rest } = l;
      return stampLayerContentHash({
        ...rest,
        id: newManualLayerId(),
        name: `${l.name} (copy)`,
        source: 'manual',
        createdAt: now,
        updatedAt: now,
      });
    });
  return [...layers, ...copies];
}

/**
 * Merge same-geometry-type layers into ONE new manual layer by concatenating
 * their coordinates, dropping the originals (bulk merge). Mixed geometry types
 * are a no-op (the model carries a single geometry per layer). The merged
 * layer inherits the first selected layer's category/colour.
 */
export function mergeSiteLayers(layers: SiteLayer[], ids: readonly string[]): SiteLayer[] {
  const pick = new Set(ids);
  const selected = layers.filter(l => pick.has(l.id));
  if (selected.length < 2) return layers;
  const first = selected[0]!;
  if (!selected.every(l => l.geometry.type === first.geometry.type)) return layers;
  const merged = stampLayerContentHash({
    ...first,
    id: newManualLayerId(),
    name: `${first.name} (merged)`,
    source: 'manual',
    geometry: {
      type: first.geometry.type,
      coordinates: selected.flatMap(l => l.geometry.coordinates),
    },
    updatedAt: new Date().toISOString(),
  });
  return [...layers.filter(l => !pick.has(l.id)), merged];
}

export function patchSiteLayer(
  layers: SiteLayer[],
  id: string,
  patch: Partial<Pick<SiteLayer, 'name' | 'category' | 'customCategory' | 'color' | 'visible' | 'description' | 'geometry'>>,
): SiteLayer[] {
  return layers.map(l => {
    if (l.id !== id) return l;
    const category = patch.category ?? l.category;
    const merged: SiteLayer = {
      ...l,
      ...patch,
      category,
      color: patch.color !== undefined
        ? normalizeLayerColor(patch.color, category)
        : l.color,
    };
    // A blank/whitespace customCategory clears the override (back to the enum).
    if (patch.customCategory !== undefined) {
      const trimmed = patch.customCategory?.trim();
      if (trimmed) merged.customCategory = trimmed;
      else delete merged.customCategory;
    }
    if (patch.geometry) {
      merged.geometry = patch.geometry;
    }
    return stampLayerContentHash(merged);
  });
}

/**
 * Feature #95 — set or clear `SiteLayer.linkedCanvasNodeId` for the
 * bidirectional KMZ-layer ↔ canvas-node link. Re-stamps the contentHash
 * (via `stampLayerContentHash`) after the write so the link survives the
 * merge/dedup pass. Pass `null`/`undefined` for `canvasNodeId` to clear.
 *
 * The link is keyed off the stable `SiteLayer.id`, so it is preserved
 * across re-imports (the canvas side mirrors it as
 * `node.data.sourceSiteLayerId`).
 */
export function setLayerCanvasLink(
  layers: SiteLayer[],
  id: string,
  canvasNodeId: string | null | undefined,
): SiteLayer[] {
  return layers.map(l => {
    if (l.id !== id) return l;
    const next: SiteLayer = { ...l };
    if (canvasNodeId) {
      next.linkedCanvasNodeId = canvasNodeId;
    } else {
      delete next.linkedCanvasNodeId;
    }
    return stampLayerContentHash(next);
  });
}

/**
 * Replace manual layers while preserving KMZ-import order for unchanged
 * KMZ rows. When `baselineLayerIds` is supplied (the layer IDs the editor
 * had seen when the draft was initialised / rebased), any layer present in
 * `previous` whose ID is NOT in `baselineLayerIds` (e.g. a canonical layer
 * that finished Phase-2 hydration after the editor opened) is preserved
 * alongside `next`, while layers in `baselineLayerIds` that the user
 * deleted from `next` stay deleted.
 */
export function mergeLayerListsForSave(
  previous: SiteLayer[] | undefined,
  next: SiteLayer[],
  baselineLayerIds?: Iterable<string>,
): SiteLayer[] {
  let combined = next;
  if (baselineLayerIds !== undefined && Array.isArray(previous) && previous.length > 0) {
    const baselineSet =
      baselineLayerIds instanceof Set ? baselineLayerIds : new Set(baselineLayerIds);
    const nextIds = new Set(next.map((layer) => layer.id));
    const newlyHydratedLayers = previous.filter(
      (layer) => !baselineSet.has(layer.id) && !nextIds.has(layer.id),
    );
    if (newlyHydratedLayers.length > 0) {
      combined = [...next, ...newlyHydratedLayers];
    }
  }
  const stamped = combined.map(stampLayerContentHash);
  return dedupSiteLayers({ siteId: 'editor-save', layers: stamped }).layers;
}

export function defaultNewLayerName(
  geometryType: 'polygon' | 'line',
  existingCount: number,
): string {
  const base = geometryType === 'polygon' ? 'Area' : 'Line';
  return `${base} ${existingCount + 1}`;
}
